import { Card } from '@bpinternal/ui-kit-next'
import { ChevronDownIcon, ChevronRightIcon, SquareCheckIcon, SquareIcon, SquareMinusIcon } from 'lucide-react'
import { customAlphabet } from 'nanoid'
import { useState } from 'react'
import CheckboxTree, { type Node as RCNode } from 'react-checkbox-tree'
import { createPortal } from 'react-dom'
import { AsyncButton } from '~/elements'
import { buildTree } from '~/features/bots/utils/buildTree'
import { AddKnowledgeSourceFooter } from '~/features/knowledge-bases/components/knowledge-source/AddKnowledgeSource/AddKnowledgeSourceFooter'
import { useDsData, useUploadWebPageSource } from '~/features/knowledge-bases/hooks/useKnowledgeSource'
import { KnowledgeSourceStepProps } from '~/features/knowledge-bases/types'
import { SyncWebsiteStepState, useSyncWebsiteContext } from './SyncWebsiteContext'

type CheckboxTreeState = {
  urls: string[]
  checked: string[]
  expanded: string[]
}

type SelectPagesStepProps = {
  workspaceId: string
  botId: string
  kbId: string
  dsId?: string
  footerRef: React.RefObject<HTMLDivElement>
  onStepChange?: (step: SyncWebsiteStepState) => void
} & Omit<KnowledgeSourceStepProps, 'onFooterActionsChange'>

export const SelectPagesStep = ({ workspaceId, botId, kbId, dsId, close, footerRef }: SelectPagesStepProps) => {
  const { dsData } = useDsData({ workspaceId, botId, kbId })
  const { mutate: uploadWebPageSource, isPending } = useUploadWebPageSource({ workspaceId, botId })
  const { discoveredUrls: allUrls, currentUrl } = useSyncWebsiteContext()

  const [websiteUrlsTree, setWebsiteUrlsTree] = useState<CheckboxTreeState>({
    urls: allUrls,
    checked: [],
    expanded: [],
  })

  const nodes = allUrls.length > 0 ? buildTree(allUrls) : []

  const onConfirm = () => {
    let newDsId = dsId || `ds-${customAlphabet('1234567890abcdef', 10)(10)}`
    for (const existingDsId of Object.keys(dsData)) {
      if (dsData[existingDsId] && currentUrl.includes(dsData[existingDsId].title)) {
        newDsId = existingDsId
      }
    }
    uploadWebPageSource({
      workspaceId,
      botId,
      kbId,
      dsId: newDsId,
      pageUrl: currentUrl,
      urls: websiteUrlsTree.checked,
      onSuccess: () => {
        close()
      },
    })
  }

  const handleCheckChange = (checked: string[], targetNode: RCNode) => {
    const currentChecked = websiteUrlsTree?.checked || []
    const isUnchecking = checked.length < currentChecked.length

    if (isUnchecking) {
      setWebsiteUrlsTree({ ...websiteUrlsTree, checked })
      return
    }

    const urlsFromNode: string[] = []

    const collectUrls = (nodeList: RCNode[]) => {
      for (const node of nodeList) {
        if (node.children) {
          collectUrls(node.children)
        } else {
          if (!currentChecked.includes(node.value)) {
            urlsFromNode.push(node.value)
          }
        }
      }
    }

    collectUrls([targetNode])
    setWebsiteUrlsTree({
      ...websiteUrlsTree,
      checked: [...currentChecked, ...urlsFromNode],
      expanded: websiteUrlsTree?.expanded,
    })
  }

  return (
    <>
      <div className="space-y-2">
        <div className="flex flex-row justify-between gap-4 text-sm text-muted-foreground">
          <p>Found {allUrls.length} pages</p>
          <p>{websiteUrlsTree.checked.length} selected</p>
        </div>
        <Card className="flex max-h-[60vh] bg-muted p-4">
          <div className="flex-1 overflow-auto">
            <CheckboxTree
              icons={{
                check: <SquareCheckIcon color="var(--icon-color)" />,
                uncheck: <SquareIcon color="var(--icon-color)" />,
                halfCheck: <SquareMinusIcon color="var(--icon-color)" />,
                expandClose: <ChevronRightIcon color="var(--icon-color)" />,
                expandOpen: <ChevronDownIcon color="var(--icon-color)" />,
                expandAll: null,
                collapseAll: null,
                parentClose: null,
                parentOpen: null,
                leaf: null,
              }}
              {...websiteUrlsTree}
              nodes={nodes ?? []}
              onExpand={(expanded) => setWebsiteUrlsTree({ ...websiteUrlsTree, expanded })}
              onCheck={handleCheckChange}
              optimisticToggle={false}
            />
          </div>
        </Card>
      </div>
      <SelectPagesFooter
        footerRef={footerRef}
        close={close}
        onConfirm={onConfirm}
        checkedCount={websiteUrlsTree.checked.length}
        isPending={isPending}
      />
    </>
  )
}

const SelectPagesFooter = ({
  footerRef,
  close,
  onConfirm,
  checkedCount,
  isPending,
}: {
  footerRef: React.RefObject<HTMLDivElement>
  close: () => void
  onConfirm: () => void
  checkedCount: number
  isPending: boolean
}) => {
  if (!footerRef.current) return null

  return createPortal(
    <AddKnowledgeSourceFooter
      linkName="Learn more about syncing websites"
      linkHref="https://botpress.com/docs/learn/reference/knowledge-base/add-sources#websites"
      showCancel={true}
      onClose={close}
      primaryAction={
        <AsyncButton
          size={'sm'}
          className="min-w-[100px]"
          loading={isPending}
          disabled={checkedCount === 0 || isPending}
          onClick={onConfirm}
        >
          Add {checkedCount} {checkedCount > 1 ? 'Pages' : 'Page'}
        </AsyncButton>
      }
    />,
    footerRef.current
  )
}
